import React, { useState, useRef, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { useWidgetProps } from "../use-widget-props";
import { useOpenAiGlobal } from "../use-openai-global";
import { useDevMode } from "../use-dev-mode";
import { History, ChevronDown } from "lucide-react";
import TabButton from "./TabButton";
import CircularProgress, { progressHasAnimatedRef } from "./CircularProgress";
import LoadingSkeleton from "./LoadingSkeleton";
import { getMaxHeight } from "./utils";

function App() {
  // Detect if we're in development mode using centralized configuration
  const { isDev } = useDevMode();

  // Mock data for development
  const mockData = {
    humanized_text:
      "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights. This phenomenon occurs when air currents of varying speeds and directions interact with the aircraft, causing it to experience brief periods of instability. While turbulence can be unsettling for passengers, modern aircraft are designed to withstand these forces safely. Pilots are trained to navigate through turbulent conditions, and the aviation industry continuously works to improve forecasting and detection methods to minimize passenger discomfort.",
    original_text:
      "Airplane turbulence is a sudden movement. It happens when air moves. Planes are safe. Pilots know what to do.",
    human_score: 0.95,
    ai_score: 0.05,
    humanization_id: "h-output-mock-001",
    created_at: new Date().toISOString(),
  };

  const props = useWidgetProps({
    humanized_text: "",
    original_text: "",
    human_score: 0,
    ai_score: 0,
    humanization_id: "",
    created_at: "",
  });

  const theme = useOpenAiGlobal("theme") || "light";
  const [initialLoading, setInitialLoading] = useState(isDev);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [textHeight, setTextHeight] = useState(null);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const textRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Use mock data in dev mode, otherwise use props
  const humanScore = Math.round(
    (isDev ? mockData.human_score : props.human_score || 0) * 100
  );
  const originalText = isDev
    ? mockData.original_text
    : props.original_text || "";
  const humanizedText = isDev
    ? mockData.humanized_text
    : props.humanized_text || "";
  const humanizationId = isDev
    ? mockData.humanization_id
    : props.humanization_id || "";
  const displayText = showOriginal ? originalText : humanizedText;

  const isLoading = isDev ? false : !humanizedText && !originalText;
  const isDark = theme === "dark";

  // Initial loading simulation (2 seconds)
  useEffect(() => {
    if (isDev && initialLoading) {
      const timer = setTimeout(() => {
        setInitialLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isDev, initialLoading]);

  // Mark as initialized once we have data (only once, never reset)
  useEffect(() => {
    if (!isLoading && humanizedText && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
    }
  }, [isLoading, humanizedText]);

  // Check if text needs expansion (more than 5 lines desktop, 9 lines mobile)
  useEffect(() => {
    if (textRef.current && displayText) {
      const checkHeight = () => {
        const isMobile =
          typeof window !== "undefined" && window.innerWidth < 768;
        const maxHeight = getMaxHeight(isMobile);
        const actualHeight = textRef.current.scrollHeight;
        const needsExp = actualHeight > maxHeight;

        setNeedsExpansion(needsExp);
        setTextHeight(actualHeight);

        if (needsExp) {
          setIsExpanded(false);
        }

        if (isInitialRender) {
          setTimeout(() => setIsInitialRender(false), 50);
        }
      };

      checkHeight();
      const timeout = setTimeout(checkHeight, 100);
      return () => clearTimeout(timeout);
    }
  }, [displayText, isInitialRender]);

  // Reset expansion when text changes - ensure it starts collapsed
  useEffect(() => {
    setIsExpanded(false);
    setIsInitialRender(true); // Reset initial render flag when text changes
  }, [displayText]);

  const handleRegenerate = async () => {
    if (!originalText) return;
    try {
      setIsRegenerating(true);
      await window.openai.sendFollowUpMessage({
        prompt: `Humanize again: ${originalText}`,
      });
    } catch (error) {
      console.error("Failed to regenerate:", error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleViewHistory = async () => {
    try {
      await window.openai.sendFollowUpMessage({
        prompt: "Show me my humanization history",
      });
    } catch (error) {
      console.error("Failed to load history:", error);
    }
  };

  const handleOpenExternal = async () => {
    if (!humanizationId) return;
    try {
      await window.openai.openExternal({
        href: `https://detect.ai/humanize/${humanizationId}`,
      });
    } catch (error) {
      console.error("Failed to open external link:", error);
    }
  };

  // Calculate max height for text container
  const getTextMaxHeight = () => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const collapsedHeight = isMobile ? "180px" : "100px";

    if (isInitialRender && textHeight === null) {
      return collapsedHeight;
    }

    if (!needsExpansion) {
      return "9999px";
    }

    if (!isExpanded) {
      return collapsedHeight;
    }

    return textHeight && textHeight > 0 ? `${textHeight + 10}px` : "9999px";
  };

  // Show loading skeleton on initial load (dev mode) or when actually loading
  if (initialLoading || isLoading) {
    return <LoadingSkeleton isDark={isDark} />;
  }

  return (
    <div
      className={`antialiased w-full overflow-hidden rounded-3xl ${
        isDark
          ? "bg-[#212121] border border-white-alpha-20"
          : "bg-white border border-black-alpha-15"
      }`}
      style={{ boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.05)" }}
    >
      {/* Header */}
      <div className={`flex flex-row items-center gap-2 pl-16 pr-8 py-6`}>
        <div className="flex-1">
          <div
            className={`font-medium text-[17px] leading-[1.35em] tracking-[-2.53%] ${
              isDark ? "text-white" : "text-black"
            }`}
          >
            Text Humanizer
          </div>
        </div>
        <button
          onClick={handleViewHistory}
          className={`p-13 rounded-lg transition-colors ${
            isDark
              ? "hover:bg-white-alpha-05 active:bg-white-alpha-10"
              : "hover:bg-black-alpha-05 active:bg-black-alpha-10"
          }`}
          aria-label="View history"
        >
          <History
            className="w-16 h-16"
            style={{ color: isDark ? "#FFFFFF" : "#000000" }}
            strokeWidth={2}
          />
        </button>
      </div>

      {/* Tab Container */}
      <div
        className={`flex flex-row items-center ${
          isDark
            ? "border-b border-white-alpha-05"
            : "border-b border-black-alpha-05"
        }`}
      >
        <TabButton
          isActive={!showOriginal}
          onClick={() => setShowOriginal(false)}
          isDark={isDark}
        >
          Humanized Result
        </TabButton>
        <TabButton
          isActive={showOriginal}
          onClick={() => setShowOriginal(true)}
          isDark={isDark}
        >
          Original Input
        </TabButton>
      </div>

      {/* Score and Description Section */}
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          showOriginal ? "max-h-0 py-0" : "max-h-96 py-12"
        }`}
      >
        <div className="px-16">
          <div className="flex items-center gap-20">
            <CircularProgress
              percentage={humanScore}
              text={true}
              shouldAnimate={
                !hasInitializedRef.current &&
                !isLoading &&
                !progressHasAnimatedRef.current
              }
              isDark={isDark}
            />
            <div className="flex-1 gap-6">
              <div
                className="font-inter font-regular transition-opacity duration-500"
                style={{
                  fontSize: "14px",
                  lineHeight: "1.43em",
                  letterSpacing: "-2%",
                  color: isDark ? "#FFFFFF" : "#0D0D0D",
                  opacity: showOriginal ? 0 : 1,
                }}
              >
                Your content now has a more natural, human touch.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Text Content Area */}
      <div
        className={`pt-6 pb-12 transition-all duration-300 ${
          showOriginal ? "pt-12" : ""
        }`}
      >
        <div className="relative">
          <div
            ref={textRef}
            className={`px-16 font-regular whitespace-pre-wrap break-words text-[14px] leading-[1.43em] tracking-[-1.29%] overflow-hidden ${
              isInitialRender
                ? ""
                : "transition-[max-height] duration-500 ease-in-out"
            } ${isDark ? "text-white" : "text-[#0D0D0D]"}`}
            style={{ maxHeight: getTextMaxHeight() }}
          >
            {displayText}
          </div>

          {/* Blur overlay for "See all" state */}
          {!isExpanded && needsExpansion && (
            <div className="absolute bottom-0 right-0 left-0 h-48 pointer-events-none transition-opacity duration-500">
              <div
                className="w-full h-full"
                style={{
                  background: isDark
                    ? "linear-gradient(180deg, rgba(33, 33, 33, 0) 0%, rgba(33, 33, 33, 0.8) 100%)"
                    : "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255) 100%)",
                  backdropFilter: "blur(0.2px)",
                }}
              />
            </div>
          )}
        </div>

        {/* See all / See less button */}
        {needsExpansion && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-center gap-1 w-full py-2 mt-2 transition-all duration-200 hover:opacity-80 active:opacity-60"
          >
            <span
              className={`font-medium text-[14px] leading-[1.43em] tracking-[-1.29%] transition-colors duration-200 ${
                isDark ? "text-[#CDCDCD]" : "text-[#5D5D5D]"
              }`}
            >
              {isExpanded ? "See less" : "See all"}
            </span>
            <div className="transition-transform duration-300 ease-in-out">
              {
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-300 ${
                    isExpanded ? "rotate-180" : "rotate-0"
                  } ${isDark ? "text-[#CDCDCD]" : "text-[#5D5D5D]"}`}
                  strokeWidth={1.5}
                />
              }
            </div>
          </button>
        )}
      </div>

      {/* Footer with Actions */}
      {!showOriginal && (
        <div
          className={`flex flex-row items-center pl-16 pr-8 py-16 gap-8 justify-between ${
            isDark
              ? "border-t border-white-alpha-05"
              : "border-t border-black-alpha-05"
          }`}
        >
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className={`px-24 py-10 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all bg-[#4D47DC] ${
              isDark
                ? "hover:bg-[#4D47DC]/90 active:bg-[#4D47DC]/80"
                : "hover:bg-[#4D47DC]/85 active:bg-[#4D47DC]/90"
            }`}
          >
            <span
              className={`font-medium text-[15px] leading-[1.6em] tracking-[-1.6%] ${
                isDark ? "text-white" : "text-[#FFFFFF]"
              }`}
            >
              {isRegenerating ? "Humanizing..." : "Humanize again"}
            </span>
          </button>
          <button
            onClick={handleOpenExternal}
            className="flex items-center justify-center gap-3 py-10 px-8"
          >
            <span
              className={`font-medium text-[14px] leading-[1.43em] tracking-[-1.29%] underline underline-offset-2 transition-colors ${
                isDark
                  ? "text-[#CDCDCD] hover:text-white-alpha-90 active:text-white-alpha-80"
                  : "text-[#5D5D5D] hover:text-black-alpha-80 active:text-black-alpha-90"
              }`}
            >
              See full result
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

// Mount the app
const rootElement = document.getElementById("humanizer-output-root");
if (rootElement) {
  createRoot(rootElement).render(<App />);
}

export default App;
